/**
 * ToneMappingEffect - unified tone mapping for WebGL and WebGPU with exclusion support.
 */

import { ToneMappingEffect as PostProcessingToneMappingEffect, ToneMappingMode } from 'postprocessing';
import * as THREE from 'three';
import { float, mix, screenUV, select, texture, uniform, vec4 } from 'three/tsl';
import { type Node, type UniformNode } from 'three/webgpu';

import { type EffectContext, IPostProcessEffect, type WebGLEffectData, type WebGPUComposeContext, type WebGPUEffectNode } from '../IPostProcessEffect.js';
import { DefaultPostprocessEffectConfigs, PostProcessPass, PostProcessRenderOrder, type ToneMappingEffectConfig } from '../PostProcessConfig.js';

import { createTonemapNode } from './TonemapShader.js';

import type { WebGPURenderer } from '../../Renderer.js';

/** Mapping from THREE.ToneMapping to postprocessing ToneMappingMode */
const TONE_MAPPING_MODE_MAP: Record<THREE.ToneMapping, ToneMappingMode> = {
  [THREE.NoToneMapping]: ToneMappingMode.LINEAR,
  [THREE.LinearToneMapping]: ToneMappingMode.LINEAR,
  [THREE.ReinhardToneMapping]: ToneMappingMode.REINHARD,
  [THREE.CineonToneMapping]: ToneMappingMode.CINEON,
  [THREE.ACESFilmicToneMapping]: ToneMappingMode.ACES_FILMIC,
  [THREE.AgXToneMapping]: ToneMappingMode.AGX,
  [THREE.NeutralToneMapping]: ToneMappingMode.NEUTRAL,
  [THREE.CustomToneMapping]: ToneMappingMode.LINEAR,
};

/**
 * Unified Tone Mapping effect for both WebGL and WebGPU.
 *
 * WebGL: Uses ToneMappingEffect from postprocessing library
 * WebGPU: Uses renderOutput() (renderer settings) OR custom TSL node with exclusion support
 */
export class ToneMappingEffect extends IPostProcessEffect<ToneMappingEffectConfig> {
  override readonly type = PostProcessPass.ToneMapping;
  override readonly order = PostProcessRenderOrder.ToneMapping;

  protected uniforms: { exposure: UniformNode<'float', number>; mode: UniformNode<'float', number>; skipNonToneMappedMaterials: UniformNode<'bool', boolean>; hasExclusions: UniformNode<'bool', boolean> } | null = null;
  private _excludedLayerDirty = true;

  override getDefaultConfig(): ToneMappingEffectConfig {
    return { ...DefaultPostprocessEffectConfigs[PostProcessPass.ToneMapping] };
  }

  /** Mark the excluded-layer assignments stale so they are recomputed on the next sync call. */
  public markExcludedLayersDirty(): void {
    this._excludedLayerDirty = true;
  }

  /**
   * Assign objects with toneMapped === false to the excluded layer when configured.
   * Called by the WebGPU pipeline before the shared exclusion mask is rendered.
   */
  @THREE.profile
  public syncSkipNonToneMappedMaterials(scene: THREE.Scene, excludedLayer: number): void {
    if (!this.uniforms?.skipNonToneMappedMaterials.value || !this._excludedLayerDirty) {
      return;
    }

    this._excludedLayerDirty = false;
    scene.traverseVisible((object: THREE.Object3D) => {
      if (object.userData.excludePostprocess) return;

      if ((object as { material?: THREE.Material | THREE.Material[] }).material &&
        (object instanceof THREE.Mesh ||
          object instanceof THREE.Line ||
          object instanceof THREE.Sprite ||
          object instanceof THREE.Points)
      ) {
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        if (materials.some(m => m?.toneMapped === false)) {
          object.layers.enable(excludedLayer);
        }
      }
    });
  }

  // ============= WebGL Implementation =============

  override createWebGLEffect(config: ToneMappingEffectConfig, context: EffectContext): WebGLEffectData {
    const defaults = this.getDefaultConfig();

    const mode = config.mode ?? defaults.mode!;
    const mappedMode = TONE_MAPPING_MODE_MAP[mode] ?? ToneMappingMode.ACES_FILMIC;

    const effect = new PostProcessingToneMappingEffect({
      mode: mappedMode,
      resolution: config.resolution ?? defaults.resolution,
      whitePoint: config.whitePoint ?? defaults.whitePoint,
      middleGrey: config.middleGrey ?? defaults.middleGrey,
      minLuminance: config.minLuminance ?? defaults.minLuminance,
      averageLuminance: config.averageLuminance ?? defaults.averageLuminance,
      adaptationRate: config.adaptationRate ?? defaults.adaptationRate,
    });

    if (config.exposure !== undefined) {
      context.renderer.toneMappingExposure = config.exposure;
    }

    return { effects: [effect] };
  }

  override applyWebGLConfig(effectData: WebGLEffectData, config: ToneMappingEffectConfig): void {
    const defaults = this.getDefaultConfig();
    const toneMappingEffect = effectData.effects[0] as PostProcessingToneMappingEffect;
    if (toneMappingEffect) {
      const mode = config.mode ?? defaults.mode!;
      toneMappingEffect.mode = TONE_MAPPING_MODE_MAP[mode] ?? ToneMappingMode.ACES_FILMIC;

      if (toneMappingEffect.adaptiveLuminanceMaterial) {
        if (config.minLuminance !== undefined) {
          toneMappingEffect.adaptiveLuminanceMaterial.minLuminance = config.minLuminance;
        }
        if (config.adaptationRate !== undefined) {
          toneMappingEffect.adaptiveLuminanceMaterial.adaptationRate = config.adaptationRate;
        }
      }
    }
  }

  // ============= WebGPU Implementation =============

  override createWebGPUNode(config: ToneMappingEffectConfig, context: EffectContext): WebGPUEffectNode {
    const defaults = this.getDefaultConfig();
    const exposure = uniform(config.exposure ?? defaults.exposure ?? 1.0);
    const mode = uniform(config.mode ?? defaults.mode ?? THREE.ACESFilmicToneMapping);
    const skipNonToneMappedMaterials = uniform(config.skipNonToneMappedMaterials ?? defaults.skipNonToneMappedMaterials ?? false);

    const hasExclusions = uniform(false);
    this.uniforms = { exposure, mode, skipNonToneMappedMaterials, hasExclusions };

    const maskTexture = context.exclusionMaskTexture;

    return {
      node: float(0),
      compose: (outputNode: Node, _ctx: WebGPUComposeContext) => {
        const tonemapped = createTonemapNode(outputNode, exposure, mode);
        const exclusionMaskTexture = _ctx.exclusionMaskTexture ?? maskTexture;
        if (!exclusionMaskTexture) {
          return vec4(tonemapped.rgb, (outputNode as Node<'vec4'>).a);
        }

        const maskRaw = texture(exclusionMaskTexture, screenUV).r;
        const outVec4 = outputNode as Node<'vec4'>;
        const withMask = vec4(mix(tonemapped.rgb, outVec4.rgb, maskRaw), outVec4.a);
        const withoutMask = vec4(tonemapped.rgb, outVec4.a);
        return select(hasExclusions, withMask, withoutMask);
      },
    };
  }

  override applyWebGPUConfig(_node: WebGPUEffectNode, config: ToneMappingEffectConfig): void {
    if (this.uniforms) {
      if (config.exposure !== undefined) this.uniforms.exposure.value = config.exposure;
      if (config.mode !== undefined) this.uniforms.mode.value = config.mode;
      if (config.skipNonToneMappedMaterials !== undefined) {
        if (config.skipNonToneMappedMaterials && !this.uniforms.skipNonToneMappedMaterials.value) {
          this._excludedLayerDirty = true;
        }
        this.uniforms.skipNonToneMappedMaterials.value = config.skipNonToneMappedMaterials;
      }
    }
  }

  override applyRendererConfig(config: ToneMappingEffectConfig, renderer: THREE.WebGLRenderer | WebGPURenderer): void {
    renderer.toneMapping = THREE.NoToneMapping;
    const defaults = this.getDefaultConfig();
    renderer.toneMappingExposure = config.exposure ?? defaults.exposure ?? 1.0;
  }

  override preRender(context: EffectContext): void {
    if (!this.uniforms) return;

    const excludedObjects = (context as { excludedObjects?: ReadonlySet<THREE.Object3D> }).excludedObjects;
    const hasManualExclusions = excludedObjects ? excludedObjects.size > 0 : false;
    const hasSkipFlag = this.uniforms.skipNonToneMappedMaterials.value;

    if (context.sharedExclusionMaskTexture !== undefined) {
      this.uniforms.hasExclusions.value = context.sharedHasExclusions === true;
      return;
    }

    if (!hasManualExclusions && !hasSkipFlag) {
      this.uniforms.hasExclusions.value = false;
      return;
    }

    this.uniforms.hasExclusions.value = true;
  }

  override requiresRebuild(oldConfig: ToneMappingEffectConfig, newConfig: ToneMappingEffectConfig): boolean {
    return oldConfig.enabled !== newConfig.enabled;
  }

  override dispose(): void {
    this.uniforms = null;
  }
}
